import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { RAW_UNIT_SUFFIX, type RawItemDto, type RawStockMovementDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

interface RawItemMovementsDrawerProps {
  rawItem: RawItemDto;
  onClose: () => void;
}

/**
 * The raw item's ledger, newest first — every OPENING/SALE/PURCHASE/WASTAGE/
 * ADJUSTMENT row with its running balance, so a wrong shelf count can be
 * traced to the entry that made it wrong.
 */
export function RawItemMovementsDrawer({ rawItem, onClose }: RawItemMovementsDrawerProps) {
  const { t } = useTranslation();
  const [movements, setMovements] = useState<RawStockMovementDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setMovements(await api.get<RawStockMovementDto[]>(`/raw-items/${rawItem.id}/movements`));
        setError(null);
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : t('store.rawItems.movementsLoadFailed'),
        );
      }
    })();
  }, [rawItem.id, t]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <aside
        onClick={(event) => event.stopPropagation()}
        className="flex h-full w-full max-w-md flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="flex items-center gap-2 border-b border-subtle p-4">
          <div>
            <h2 className="text-lg font-semibold text-content">{t('store.rawItems.movements')}</h2>
            <p className="text-sm text-content-secondary">
              {rawItem.name} ·{' '}
              {t('store.rawItems.inStock', {
                count: rawItem.stockQuantity,
                unit: RAW_UNIT_SUFFIX[rawItem.unit],
              })}
            </p>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('store.rawItems.close')}
            title={t('store.rawItems.close')}
            className="grid h-8 w-8 place-items-center rounded-control border border-subtle text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : movements === null ? (
            <p className="text-sm text-content-muted">{t('common.loading')}</p>
          ) : (
            <MovementsList movements={movements} unitSuffix={RAW_UNIT_SUFFIX[rawItem.unit]} />
          )}
        </div>
      </aside>
    </div>
  );
}

/** Presentational and exported so the ledger rendering rules are testable. */
export function MovementsList({
  movements,
  unitSuffix,
}: {
  movements: readonly RawStockMovementDto[];
  unitSuffix: string;
}) {
  const { t } = useTranslation();

  if (movements.length === 0) {
    return <p className="text-sm text-content-muted">{t('store.rawItems.movementsEmpty')}</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {movements.map((movement) => (
        <li
          key={movement.id}
          className="rounded-card border border-subtle px-3 py-2"
          data-reason={movement.reason}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-content">
              {t(`store.rawItems.reasons.${movement.reason}`)}
            </span>
            <span
              className={`tabular text-sm ${movement.delta < 0 ? 'text-danger' : 'text-content'}`}
            >
              {movement.delta > 0 ? '+' : ''}
              {movement.delta} {unitSuffix}
            </span>
            <div className="flex-1" />
            <span
              className={`tabular text-xs ${
                movement.balanceAfter < 0 ? 'text-danger' : 'text-content-secondary'
              }`}
            >
              {t('store.rawItems.balanceAfter', {
                count: movement.balanceAfter,
                unit: unitSuffix,
              })}
            </span>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2 text-xs text-content-muted">
            <span className="tabular">{movement.businessDate}</span>
            {movement.employeeName ? <span>{movement.employeeName}</span> : null}
            {movement.note ? <span>· {movement.note}</span> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
